import { deleteApiV1ThreeLetterCodeClientAdminsClientAdminUsersId } from '@fukuri-admins/apis/client/gen/endpoints';
import type { GetApiV1ThreeLetterCodeClientAdminsClientAdminUsers200 } from '@fukuri-admins/apis/client/gen/model';
import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEnvFeatureFlag } from '~/utils/getEnvFeatureFlag';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import { useGetClientAdminUsers } from './useGetClientAdminUsers';

type User = {
  id: number;
  name: string;
  email: string;
  createdAtLabel: string;
  isCurrentUser: boolean;
};

type IUseAdminUserList = () => {
  state: {
    users: GetApiV1ThreeLetterCodeClientAdminsClientAdminUsers200;
    currentPage: number;
    totalPage: number;
    totalCount: number;
    enableStaging: boolean;
    isOpenCreateModal: boolean;
    isOpenEditModal: boolean;
    selectedUser: User | null;
  };
  actions: {
    handlePageChange: (page: number) => void;
    handleEdit: (user: User) => void;
    handleDelete: (user: User) => void;
    handleCloseEditModal: () => void;
    handleOpenCreateModal: () => void;
    handleCloseCreateModal: () => void;
    handleMutateUsers: () => void;
  };
};

export const useAdminUserList: IUseAdminUserList = () => {
  const [filterParams, setFilterParams] = useSearchParams();
  const { enableStaging } = getEnvFeatureFlag();
  const [isOpenCreateModal, setIsOpenCreateModal] = useState<boolean>(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const threeLetterCode = getThreeLetterCode() || '';
  const { showToast } = useToast();
  const { showAlertDialog } = useAlertDialog();
  const navigate = useNavigate();

  const rawPage = Number.parseInt(filterParams.get('page') ?? '1', 10);
  const currentPage = Number.isNaN(rawPage) || rawPage < 1 ? 1 : rawPage;

  const {
    data: users,
    totalPage,
    totalCount,
    mutate,
  } = useGetClientAdminUsers({ threeLetterCode, page: String(currentPage) });

  /** 編集ページへ遷移、または編集モーダルを開く */
  const handleEdit = (user: User) => {
    if (enableStaging) {
      navigate(replaceRouteParams(ROUTES.ADMINS.EDIT, { id: user.id }));
      return;
    }
    setSelectedUser(user);
    setIsOpenEditModal(true);
  };
  /** 削除確認ダイアログを表示 */
  const handleDelete = (user: User) => {
    setSelectedUser(user);

    showAlertDialog({
      title: '管理者を削除しますか？',
      message: `${user.name} を管理者から削除します。削除すると元に戻すことはできません。`,
      confirmText: '削除',
      confirmButtonVariant: 'destructive',
      onConfirm: () => executeDelete(user),
    });
  };
  /** 削除を実行 */
  const executeDelete = async (user: User) => {
    try {
      await deleteApiV1ThreeLetterCodeClientAdminsClientAdminUsersId(threeLetterCode, user.id);
      showToast({
        message: '管理者を削除しました。',
        status: 'success',
      });
      mutate();
      setSelectedUser(null);
    } catch {
      showToast({
        message: '管理者の削除に失敗しました。',
        status: 'danger',
      });
    }
  };
  /** 編集モーダルを閉じる */
  const handleCloseEditModal = () => setIsOpenEditModal(false);
  /** 新規追加モーダルを開く */
  const handleOpenCreateModal = () => setIsOpenCreateModal(true);
  /** 新規追加モーダルを閉じる */
  const handleCloseCreateModal = () => setIsOpenCreateModal(false);

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', page.toString());
    setFilterParams(newParams);
  };

  const handleMutateUsers = async () => await mutate();

  return {
    state: {
      users,
      currentPage,
      totalPage,
      totalCount,
      enableStaging,
      isOpenCreateModal,
      isOpenEditModal,
      selectedUser,
    },
    actions: {
      handlePageChange,
      handleEdit,
      handleDelete,
      handleCloseEditModal,
      handleOpenCreateModal,
      handleCloseCreateModal,
      handleMutateUsers,
    },
  };
};
